import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { sessionApi } from '../services/apiService';
import { telHref, mailtoHref } from '../lib/renewal';

/**
 * [SEC-2026-06] Sticky, non-dismissable banner shown to every dashboard when the
 * school's subscription is expired/suspended (read-only mode). Dark-mode aware.
 *
 * [PHASE-5 2026-10] The contact comes from platform settings (edited by the
 * super admin), never from a hardcoded address. Since Phase 5 an expired
 * school is locked out on the server (renewal screen for its admin), so this
 * banner is the fallback for the read-only 403 that still guards mutations.
 */
const SubscriptionBanner = () => {
  const { t } = useTranslation();
  const isReadOnly = useReadOnlyMode();
  const [contact, setContact] = useState(null);

  useEffect(() => {
    if (!isReadOnly) return undefined;
    let cancelled = false;
    sessionApi.getPlatformContact()
      .then((c) => { if (!cancelled) setContact(c); })
      .catch(() => { /* banner still shows, without a contact link */ });
    return () => { cancelled = true; };
  }, [isReadOnly]);

  if (!isReadOnly) return null;

  const phone = contact && contact.phone;
  const emailHref = mailtoHref(contact && contact.email);
  const phoneHref = telHref(phone);

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-2 text-xs sm:text-sm font-semibold text-center bg-amber-400 text-amber-950 dark:bg-amber-500 dark:text-amber-950 border-b border-amber-600 shadow"
    >
      <span>🔒 {t('subscription_readonly_banner')}</span>
      {emailHref && (
        <a
          href={emailHref}
          className="underline rounded px-2 py-0.5 bg-amber-900 text-amber-50 hover:bg-amber-950 transition-colors"
        >
          {t('subscription_renew')}
        </a>
      )}
      {phoneHref && (
        <a
          href={phoneHref}
          className="underline rounded px-2 py-0.5 bg-amber-900 text-amber-50 hover:bg-amber-950 transition-colors"
        >
          {t('subscription_call')} <span dir="ltr">{phone}</span>
        </a>
      )}
    </div>
  );
};

export default SubscriptionBanner;
